import { BlockOutlined, Cancel, CheckCircle, ChevronRight, Groups, HourglassEmpty, Language, PauseCircle, PhoneIphone, Report } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFecth';
import ListPageLayout from '../../../components/ListPageLayout';
import { LoadingContent } from '../../../components/Loading';
import DialogMigrate from '../../DialogMigrate';
import { SearchDrawerClubs } from './components/SearchDrawerClubs';

const MULTI_QUERY_KEYS = new Set(['functions', 'customs', 'additionals']);

function paramsFromSearchParams(searchParams) {
    const params = {};
    for (const key of new Set(searchParams.keys())) {
        const all = searchParams.getAll(key).filter(v => v !== '');
        if (all.length === 0) continue;
        params[key] = MULTI_QUERY_KEYS.has(key) ? all : all.length === 1 ? all[0] : all;
    }
    return params;
}

const SIGNATURE_STATUS_META = {
    WAITING: { label: 'Aguardando', color: 'warning', icon: HourglassEmpty },
    ACTIVE: { label: 'Ativa', color: 'success', icon: CheckCircle },
    EXPIRED: { label: 'Vencida', color: 'error', icon: Report },
    CANCELLED: { label: 'Cancelada', color: 'default', icon: Cancel },
    CANCELED: { label: 'Cancelada', color: 'default', icon: Cancel },
    INACTIVE: { label: 'Inativa', color: 'default', icon: PauseCircle },
    NONE: { label: 'Sem assinatura', color: 'default', icon: PauseCircle },
};

const DOMAIN_STATUS_LABELS = {
    processing: 'Verificando DNS',
    provisioning_hostname: 'Emitindo certificado',
    provisioning_route: 'Conectando ao clube',
    verifying: 'Finalizando ativação',
    active: 'Ativo',
    removing: 'Removendo domínio',
    failed_cname: 'CNAME ausente ou incorreto',
    failed: 'Não foi possível concluir',
    pending: 'Pendente',
    pending_validation: 'Em validação',
    blocked: 'Bloqueado',
    suspended: 'Suspenso',
    deleted: 'Removido',
    error: 'Erro',
    unknown: 'Sem status',
    none: 'Sem domínio',
};

function domainBreakdownLabel(key) {
    return DOMAIN_STATUS_LABELS[key] || key;
}

function subscriptionBreakdownLabel(key) {
    return SIGNATURE_STATUS_META[key]?.label || key;
}

function StatCard({ title, value, icon, color = 'text.primary', breakdown = [] }) {
    return (
        <Card variant="outlined" sx={{ minWidth: 0, boxShadow: 'none' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {title}
                        </Typography>
                        <Box sx={{ color: 'action.active', opacity: 0.5, display: 'flex' }}>{icon}</Box>
                    </Stack>
                    <Typography variant="h3" fontWeight={600} sx={{ color }}>
                        {(value ?? 0).toLocaleString('pt-BR')}
                    </Typography>
                    {breakdown.length > 0 ? (
                        <Stack spacing={0.5}>
                            {breakdown.map(item => (
                                <Stack key={item.key} direction="row" justifyContent="space-between" gap={1}>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {item.label}
                                    </Typography>
                                    <Typography variant="caption" fontWeight={600}>
                                        {item.count.toLocaleString('pt-BR')}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );
}

function subscriptionStatusChip(item) {
    const signature = item?.Signature;
    if (!signature) return { label: 'Sem assinatura', color: 'default', icon: PauseCircle };

    const fromStatus = SIGNATURE_STATUS_META[signature.status];
    if (fromStatus) return fromStatus;

    const due = signature.dueDateCycle;
    const isExpired = due && new Date(due) < new Date();
    if (['ACTIVE', 'CONFIRMED', 'RECEIVED'].includes(signature.paymentStatus) && !isExpired) {
        return SIGNATURE_STATUS_META.ACTIVE;
    }
    if (isExpired || signature.paymentStatus === 'OVERDUE') return SIGNATURE_STATUS_META.EXPIRED;
    if (['PENDING', 'WAITING'].includes(signature.paymentStatus)) return SIGNATURE_STATUS_META.WAITING;
    return { label: signature.status || 'Inativa', color: 'default', icon: PauseCircle };
}

function clubLogoSx(primaryColor) {
    return {
        width: 96,
        height: 48,
        bgcolor: primaryColor || 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        '& .MuiAvatar-img': { objectFit: 'contain' },
    };
}

function ClubItem({ item, viewMode }) {
    const subscription = subscriptionStatusChip(item);
    const StatusIcon = subscription.icon;
    const planName = item?.Contract?.contractData?.plan?.name || '—';
    const domain = item?.domain || 'Sem domínio';

    const statusChip = (
        <Chip
            size="small"
            icon={StatusIcon ? <StatusIcon /> : undefined}
            color={subscription.color}
            label={subscription.label}
        />
    );

    if (viewMode === 'cards') {
        return (
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea component={Link} to={`${item.id}`} sx={{ p: 2, height: '100%' }}>
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar src={item.logo} variant="rounded" alt={item?.name} sx={clubLogoSx(item?.primaryColor)} />
                            <Box minWidth={0}>
                                <Typography fontWeight={700} noWrap>
                                    {item?.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    #{item?.id}
                                </Typography>
                            </Box>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {item?.businessName || '—'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {domain}
                        </Typography>
                        <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                            {statusChip}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            Plano: {planName}
                        </Typography>
                    </Stack>
                </CardActionArea>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <CardActionArea component={Link} to={`${item.id}`} sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Avatar src={item.logo} variant="rounded" alt={item?.name} sx={clubLogoSx(item?.primaryColor)} />
                    <Stack spacing={0.5} sx={{ flex: 1.4, minWidth: 0 }}>
                        <Typography fontWeight={700} noWrap>
                            #{item?.id} · {item?.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {item?.businessName || '—'}
                        </Typography>
                    </Stack>
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                            Plano
                        </Typography>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {planName}
                        </Typography>
                    </Stack>
                    <Stack spacing={0.25} sx={{ flex: 1.2, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                            Domínio
                        </Typography>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {domain}
                        </Typography>
                    </Stack>
                    {statusChip}
                    <ChevronRight sx={{ color: 'action.active', display: { xs: 'none', sm: 'block' } }} />
                </Stack>
            </CardActionArea>
        </Card>
    );
}

export default function Clubs() {
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const { data, mutate, isFetching } = useFetch('clubs', { params: paramsFromSearchParams(searchParams) });
    const { data: stats } = useFetch('clubs/stats');

    const domainBreakdown = (stats?.domain?.breakdown || []).map(item => ({
        ...item,
        label: domainBreakdownLabel(item.key),
    }));
    const subscriptionBreakdown = (stats?.subscription?.breakdown || []).map(item => ({
        ...item,
        label: subscriptionBreakdownLabel(item.key),
    }));

    const handlePage = (_, page) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('page', page);
            return next;
        });
    };

    const handlePageSizeChange = nextSize => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('per_page', String(nextSize));
            next.set('page', '1');
            return next;
        });
    };

    const pageSize = Number(searchParams.get('per_page')) || 10;

    const hasFilters = Array.from(searchParams.entries()).some(
        ([key, value]) => value !== '' && value !== null && key !== 'page' && key !== 'per_page',
    );

    const handleClearFilters = () => setSearchParams({});

    return (
        <>
            <ListPageLayout
                title="Clubes"
                subtitle="Gerencie os clubes de benefícios."
                counts={[{ label: 'clubes', value: data?.numRows ?? data?.rows?.length ?? 0, color: 'primary' }]}
                loading={isFetching && !data}
                filterActive={hasFilters}
                onClearFilters={hasFilters ? handleClearFilters : null}
                onFiltersClick={() => setDrawerOpen(true)}
                viewStorageKey="admin-clubs-view"
                intro={
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        <StatCard
                            title="Total de clubes"
                            value={stats?.total}
                            icon={<Groups />}
                            breakdown={[{ key: 'blocked', label: 'Bloqueados', count: stats?.blocked ?? 0 }]}
                        />
                        <StatCard
                            title="Domínio ativo"
                            value={stats?.domain?.active}
                            icon={<Language />}
                            color="success.main"
                            breakdown={domainBreakdown}
                        />
                        <StatCard
                            title="Assinatura ativa"
                            value={stats?.subscription?.active}
                            icon={<CheckCircle />}
                            color="success.main"
                            breakdown={subscriptionBreakdown}
                        />
                        <StatCard
                            title="Com app"
                            value={stats?.app?.custom}
                            icon={<PhoneIphone />}
                            breakdown={[{ key: 'active', label: 'App ativo', count: stats?.app?.active ?? 0 }]}
                        />
                    </Box>
                }
                isEmpty={!isFetching && data?.rows?.length === 0}
                empty={
                    <Stack direction="row" justifyContent="center" gap={1} alignItems="center">
                        <Typography>Nenhum clube encontrado.</Typography>
                        <BlockOutlined />
                    </Stack>
                }
                pagination={{
                    page: data?.page || 1,
                    pages: data?.pages || 1,
                    total: data?.numRows ?? 0,
                    pageSize,
                    onChange: handlePage,
                    onPageSizeChange: handlePageSizeChange,
                    itemLabel: (data?.numRows ?? 0) === 1 ? 'clube' : 'clubes',
                }}
            >
                {({ viewMode }) =>
                    isFetching && !data ? (
                        <LoadingContent />
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                display: viewMode === 'cards' ? 'grid' : 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                gridTemplateColumns:
                                    viewMode === 'cards'
                                        ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
                                        : undefined,
                            }}
                        >
                            {data?.rows?.map(item => (
                                <ClubItem key={item.id} item={item} viewMode={viewMode} />
                            ))}
                        </Box>
                    )
                }
            </ListPageLayout>

            <DialogMigrate mutate={mutate} from="club" />
            <SearchDrawerClubs open={isDrawerOpen} onClose={() => setDrawerOpen(false)} onClearFilters={handleClearFilters} />
        </>
    );
}
